/**
 * /admin/taxonomy — Admin Taxonomy (Categories & Tags) page.
 *
 * Binding contract: `docs/ia/admin-taxonomy.md`.
 *
 * Archetype: **dual-panel taxonomy editor** — two equal-status entity
 * managers placed side-by-side. The page answers "manage the two axes
 * I use to slice questions" without forcing a tab-switch. The SME chose
 * a dual-panel layout rather than a tabbed UI because tabs hide one
 * entity behind the other, and the admin's mental model is "I'm tidying
 * both at once." (See `docs/ia/admin-taxonomy.md` § Editorial intent.)
 *
 * Substrate (15-FG/37): <AdminLayout> wraps <Shell mode='admin'
 * densityMode='workspace'>. Primitives only from `@/components/ui`
 * (Heading / Eyebrow / Button / Card / Input). design.md tokens only —
 * no `bg-bg-card` / `bg-surface` / `rounded-2xl` / chromatic borders.
 *
 * v1 data wiring carve-out: the page renders against local mock state.
 * The shapes mirror what `/api/taxonomy/categories` and `/api/taxonomy/tags`
 * already expose (per `packages/web/src/state/questions/api.ts:172-191`).
 * Wiring is a follow-up — flagged in the issue Outcome.
 *
 * Cross-cutting invariants:
 *   - Inv 12 (no console.log) — none introduced.
 *   - Inv 13 (no `any` / @ts-ignore) — none.
 *   - Inv 14 (internal nav via <Link>) — no internal nav on this page.
 *   - Inv 15 (atoms from barrel) — Heading / Button / Card / Input / Eyebrow only.
 */

import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { AdminLayout } from '../layout.tsx';
import { Button, Card, Heading, Input } from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  listCategories,
  listTags,
  createTag,
} from '../../../state/questions/api.ts';

// ─── Types ────────────────────────────────────────────────────────────────

interface SubcategoryRow {
  id: string;
  name: string;
  count: number;
}

interface CategoryRow {
  id: string;
  name: string;
  count: number;
  subcategories: SubcategoryRow[];
}

interface TagRow {
  id: string;
  name: string;
  count: number;
}

// CategoryPayload now includes `count` (questions per category).
// TagPayload now includes `count` (questions per tag).
// Subcategories remain empty until lazy-loaded per category via
// GET /api/taxonomy/categories/:id/subcategories.

// ─── Formatting ───────────────────────────────────────────────────────────

const EM_DASH = '—';

function formatInt(n: number): string {
  return n.toLocaleString('en-US');
}

// ─── Tokenised classnames (composed via cn() so eslint-plugin-tailwindcss
//     static analysis preserves design.md tokens) ────────────────────────

const PANEL_HEADER_CLASSES = cn(
  'flex items-center justify-between',
  'border-b border-line',
  'px-4 py-3',
);

const ROW_BASE_CLASSES = cn(
  'flex items-center justify-between gap-3',
  'border-b border-line last:border-b-0',
  'px-4 py-2.5',
  'transition-base',
  'hover:bg-bg-warm',
);

const COUNT_CLASSES = cn('font-mono text-mono text-muted');

const ADD_BAR_CLASSES = cn(
  'flex items-center gap-2',
  'border-b border-line bg-bg-warm',
  'px-4 py-3',
);

// ─── Panel — co-located, exposes a labelled landmark ──────────────────────

interface PanelProps {
  id: string;
  title: string;
  addCtaLabel: string;
  onAddClick: () => void;
  children: ReactNode;
}

function Panel({
  id,
  title,
  addCtaLabel,
  onAddClick,
  children,
}: PanelProps): ReactElement {
  const headingId = `${id}-heading`;
  return (
    <Card variant="default" className="p-0">
      <section role="region" aria-labelledby={headingId}>
        <header className={PANEL_HEADER_CLASSES}>
          <Heading
            id={headingId}
            level="h2"
            className="text-title font-medium text-ink"
          >
            {title}
          </Heading>
          <Button
            variant="primary"
            size="sm"
            type="button"
            onClick={onAddClick}
          >
            {addCtaLabel}
          </Button>
        </header>
        {children}
      </section>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────

export interface AdminTaxonomyPageProps {
  /** Test hook — keep the page in its loading state so AC5 can observe the
   *  em-dash placeholders. Production callers omit this; the hook below
   *  resolves to ready state on mount. */
  initialLoading?: boolean;
}

export function AdminTaxonomyPage({
  initialLoading = false,
}: AdminTaxonomyPageProps = {}): ReactElement {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [tags, setTags] = useState<TagRow[]>([]);

  // Inline-add state per panel.
  const [showAddCategory, setShowAddCategory] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>('');
  const [showAddTag, setShowAddTag] = useState<boolean>(false);
  const [newTagName, setNewTagName] = useState<string>('');

  useEffect(() => {
    // When the test hook forces the loading state, skip the real fetch so the
    // loading placeholders remain visible for AC5.
    if (initialLoading) {
      setIsLoading(true);
      return;
    }

    let cancelled = false;
    void (async () => {
      setIsLoading(true);
      const [catsEnv, tagsEnv] = await Promise.all([
        listCategories(),
        listTags(),
      ]);
      if (cancelled) return;
      if (catsEnv.ok) {
        setCategories(
          catsEnv.data.map((c) => ({
            id: c.id,
            name: c.name,
            count: c.count,
            subcategories: [],
          })),
        );
      }
      if (tagsEnv.ok) {
        setTags(tagsEnv.data.map((t) => ({ id: t.id, name: t.name, count: t.count })));
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [initialLoading]);

  const onAddCategory = (): void => {
    setShowAddCategory(true);
  };

  const onSaveCategory = (): void => {
    const trimmed = newCategoryName.trim();
    if (trimmed.length === 0) {
      setShowAddCategory(false);
      return;
    }
    const id = `cat-${Date.now().toString(36)}`;
    setCategories((prev) => [
      ...prev,
      { id, name: trimmed, count: 0, subcategories: [] },
    ]);
    setNewCategoryName('');
    setShowAddCategory(false);
  };

  const onCancelAddCategory = (): void => {
    setNewCategoryName('');
    setShowAddCategory(false);
  };

  const onRemoveCategory = (id: string): void => {
    setCategories((prev) => prev.filter((c) => c.id !== id));
  };

  const onRemoveSubcategory = (categoryId: string, subId: string): void => {
    setCategories((prev) =>
      prev.map((c) =>
        c.id === categoryId
          ? { ...c, subcategories: c.subcategories.filter((s) => s.id !== subId) }
          : c,
      ),
    );
  };

  const onAddTag = (): void => {
    setShowAddTag(true);
  };

  const onSaveTag = (): void => {
    const trimmed = newTagName.trim();
    if (trimmed.length === 0) {
      setShowAddTag(false);
      return;
    }
    // Optimistic update — append immediately; the real API create confirms.
    const tempId = `tag-${Date.now().toString(36)}`;
    setTags((prev) => [...prev, { id: tempId, name: trimmed, count: 0 }]);
    setNewTagName('');
    setShowAddTag(false);
    // Persist to backend and replace the temp row with the real ID.
    void (async () => {
      const env = await createTag(trimmed);
      if (env.ok) {
        setTags((prev) =>
          prev.map((t) =>
            t.id === tempId ? { id: env.data.id, name: env.data.name, count: 0 } : t,
          ),
        );
      }
    })();
  };

  const onCancelAddTag = (): void => {
    setNewTagName('');
    setShowAddTag(false);
  };

  const onRemoveTag = (id: string): void => {
    setTags((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <AdminLayout title="Categories & Tags" eyebrow="Admin · Taxonomy">
      {/* Sub-label — Polish 1 numeric-or-prose typography. Mirrors the
          dashboard's eyebrow voice but in sentence case per the contract
          ("Organise your question bank"). */}
      <p className="mt-2 font-mono text-mono text-muted">
        Organise your question bank
      </p>

      {/* Two equal-status side-by-side panels — Categories LEFT, Tags RIGHT.
          1:1 grid at the lg breakpoint, stacks vertically below per the
          contract's Extension rule. 20px gap per Polish 2 (`gap-5`). */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* ─── Categories panel ─────────────────────────────────────────── */}
        <Panel
          id="categories"
          title="Categories & Subcategories"
          addCtaLabel="+ Add Category"
          onAddClick={onAddCategory}
        >
          {showAddCategory ? (
            <div className={ADD_BAR_CLASSES}>
              <div className="min-w-0 flex-1">
                <label htmlFor="new-category-name" className="sr-only">
                  New category name
                </label>
                <Input
                  id="new-category-name"
                  type="text"
                  placeholder="Category name…"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  autoFocus
                />
              </div>
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={onSaveCategory}
              >
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={onCancelAddCategory}
              >
                Cancel
              </Button>
            </div>
          ) : null}

          {isLoading ? (
            <div className="px-4 py-10 text-center font-mono text-mono text-muted">
              <span aria-live="polite">{EM_DASH}</span>
            </div>
          ) : categories.length === 0 ? (
            <div className="px-4 py-10 text-center font-sans text-small text-muted">
              No categories yet — add one with the button above.
            </div>
          ) : (
            <ul role="list" className="flex flex-col">
              {categories.map((cat) => (
                <CategoryRowItem
                  key={cat.id}
                  category={cat}
                  onRemove={() => onRemoveCategory(cat.id)}
                  onRemoveSubcategory={(subId) =>
                    onRemoveSubcategory(cat.id, subId)
                  }
                />
              ))}
            </ul>
          )}
        </Panel>

        {/* ─── Tags panel ───────────────────────────────────────────────── */}
        <Panel
          id="tags"
          title="Tags"
          addCtaLabel="+ Add Tag"
          onAddClick={onAddTag}
        >
          {showAddTag ? (
            <div className={ADD_BAR_CLASSES}>
              <div className="min-w-0 flex-1">
                <label htmlFor="new-tag-name" className="sr-only">
                  New tag name
                </label>
                <Input
                  id="new-tag-name"
                  type="text"
                  placeholder="Tag name…"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  autoFocus
                />
              </div>
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={onSaveTag}
              >
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={onCancelAddTag}
              >
                Cancel
              </Button>
            </div>
          ) : null}

          {isLoading ? (
            <div className="px-4 py-10 text-center font-mono text-mono text-muted">
              <span aria-live="polite">{EM_DASH}</span>
            </div>
          ) : tags.length === 0 ? (
            <div className="px-4 py-10 text-center font-sans text-small text-muted">
              No tags yet — add one with the button above.
            </div>
          ) : (
            <ul role="list" className="flex flex-col">
              {tags.map((tag) => (
                <li
                  key={tag.id}
                  className={ROW_BASE_CLASSES}
                  data-depth="0"
                >
                  <span className="min-w-0 flex-1 truncate font-sans text-body text-ink">
                    {tag.name}
                  </span>
                  {tag.count > 0 ? (
                    <span className={COUNT_CLASSES}>{formatInt(tag.count)}</span>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => onRemoveTag(tag.id)}
                    aria-label={`Remove ${tag.name}`}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </AdminLayout>
  );
}

// ─── Category row (with one level of subcategory nesting) ────────────────

interface CategoryRowItemProps {
  category: CategoryRow;
  onRemove: () => void;
  onRemoveSubcategory: (subId: string) => void;
}

function CategoryRowItem({
  category,
  onRemove,
  onRemoveSubcategory,
}: CategoryRowItemProps): ReactElement {
  return (
    <>
      <li className={ROW_BASE_CLASSES} data-depth="0">
        <span className="min-w-0 flex-1 truncate font-sans text-body text-ink">
          {category.name}
        </span>
        {category.count > 0 ? (
          <span className={COUNT_CLASSES}>{formatInt(category.count)}</span>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${category.name}`}
        >
          Remove
        </Button>
      </li>
      {category.subcategories.map((sub) => (
        <li
          key={sub.id}
          className={cn(ROW_BASE_CLASSES, 'pl-10')}
          data-depth="1"
        >
          <span className="min-w-0 flex-1 truncate font-sans text-body text-muted">
            {sub.name}
          </span>
          {sub.count > 0 ? (
            <span className={COUNT_CLASSES}>{formatInt(sub.count)}</span>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => onRemoveSubcategory(sub.id)}
            aria-label={`Remove ${sub.name}`}
          >
            Remove
          </Button>
        </li>
      ))}
    </>
  );
}
